import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@/lib/api/client';
import { searchTasks } from '@/services/taskService';
import type { Task } from '@/types/api';

/**
 * Search is a separate query from the board, deliberately.
 *
 * The board is five paginated columns; results are one flat list ordered by relevance.
 * Filtering the loaded pages client-side would only ever search what you had already
 * scrolled to — the opposite of what a search box promises.
 *
 * Visibility comes free: this hits the user route, so the same predicate that scopes
 * the board scopes the results. Nobody can search their way to someone else's card.
 */
export function useTaskSearch(term: string, assigneeId?: string) {
  const query = useDebounced(term.trim(), 500);
  const active = query.length >= 2;

  const result = useQuery<{ items: Task[] }, ApiError>({
    // The scope is part of the key: an admin who switches to their own board and
    // repeats a search must not be handed the company-wide answer from cache.
    queryKey: ['taskSearch', assigneeId ?? 'me', query],
    queryFn: () => searchTasks({ q: query, assigneeId }),
    enabled: active,
    /*
      Holding the previous list while the next loads stops the panel flashing empty
      between keystrokes, which reads as "no results" for a frame.

      Only while there IS a term. Carrying it forward unconditionally kept the last
      results alive after the box was emptied — the query key changed to an empty
      string, the query was disabled, and placeholderData handed the old data straight
      back. That is how a closed panel reopened showing the previous search.
    */
    ...(active ? { placeholderData: (previous: { items: Task[] } | undefined) => previous } : {}),
  });

  return { ...result, query, active };
}

/**
 * 500ms. Long enough that a whole word is one request rather than seven, and the
 * trigram scan is the expensive half — a substring search with a leading wildcard is
 * the most costly thing the board asks Postgres to do.
 */
function useDebounced(value: string, delay: number): string {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    /*
      Clearing is not a search, so it does not wait.
      Debouncing an empty string means the previous term — and its results — stay live
      for another half second after the box is emptied, which is exactly long enough to
      reopen the panel and see the last thing you searched for.
    */
    if (value.length === 0) {
      setSettled('');
      return;
    }

    const timer = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return settled;
}
